/**
 * @file Constants used throughout the billing application
 */

/**
 * Escape special regex characters in a string
 * @private
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe to use in RegExp constructor
 */
function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Default Ghost Pro domain suffix
 * Used as fallback when site.pro_domain is not available
 * @type {string}
 */
export const DEFAULT_PRO_DOMAIN = 'ghost.io';

/**
 * Ghost Pro redirect server IP address
 * Used for A records when setting up custom domains with www subdomain
 * This server handles redirects from root domain to www subdomain
 * @type {string}
 */
export const GHOST_REDIRECT_SERVER_IP = '178.128.137.126';

/**
 * HTTP client request timeout in milliseconds
 * Set to 18 seconds to match Netlify function timeout limits
 * @type {number}
 */
export const HTTP_REQUEST_TIMEOUT_MS = 18000;

/**
 * Maximum number of retry attempts for failed HTTP requests
 * Will retry on 5xx errors and network failures
 * @type {number}
 */
export const HTTP_RETRY_LIMIT = 3;

/**
 * Maximum retry delay in milliseconds
 * Used as fallback when retry count exceeds the number of defined delays
 * @type {number}
 */
export const HTTP_RETRY_MAX_DELAY_MS = 4000;

/**
 * HTTP retry delay values in milliseconds for exponential backoff
 * Each retry attempt uses the corresponding delay: 1st retry = 1s, 2nd = 2s, 3rd = 4s
 * @type {number[]}
 */
export const HTTP_RETRY_DELAYS_MS = [1000, 2000, HTTP_RETRY_MAX_DELAY_MS];

/**
 * Ghost Admin billing app path
 * Used to construct URLs to the billing app section of Ghost Admin
 * @type {string}
 */
export const GHOST_ADMIN_BILLING_APP_PATH = '/ghost/#/pro';

/**
 * Entry product name (lowest tier)
 * Used as default product when no other product is specified,
 * and to determine certain limitations (such as setting up a
 * custom sending domain)
 * @type {string}
 */
export const LOWEST_TIER_PRODUCT_NAME = 'Starter';

/**
 * Billing periods, both display and API/Stripe values
 * @type {object}
 */
export const BILLING_PERIODS = {
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
    MONTH: 'month',
    YEAR: 'year',
};

/**
 * Default billing period
 * Used to default the yearly billing period for new subscriptions (trial -> paid)
 * @type {string}
 */
export const DEFAULT_BILLING_PERIOD = BILLING_PERIODS.YEAR;

/**
 * Shared identifier for managed subdirectory limits, add-ons, and blockers.
 * @type {string}
 */
export const SUBDIRECTORY_IDENTIFIER = 'subdirectory';

/**
 * Display name used by Daisy for the managed subdirectory add-on.
 * @type {string}
 */
export const SUBDIRECTORY_ADDON_NAME = 'Subdirectory install';

/**
 * Categories used to describe why a plan cannot be selected.
 * @type {{SELF_SERVE: 'self-serve', AUDIENCE: 'audience', STRUCTURAL: 'structural', CURRENT_PLAN: 'current-plan'}}
 */
export const PLAN_BLOCKER_CATEGORIES = {
    SELF_SERVE: 'self-serve',
    AUDIENCE: 'audience',
    STRUCTURAL: 'structural',
    CURRENT_PLAN: 'current-plan',
};

/**
 * Presentation modes for the plan requirements modal.
 * @type {{STANDARD: 'standard', AUDIENCE: 'audience'}}
 */
export const PLAN_REQUIREMENTS_MODES = {
    STANDARD: 'standard',
    AUDIENCE: 'audience',
};

/**
 * Semantic Ghost Admin destinations the Billing App may request navigation to.
 * Ghost Admin owns the destination-to-route mapping (see GVA-868); the Billing
 * App must only ever send these identifiers, never raw Admin routes or URLs.
 * @type {{THEME: 'theme', ANALYTICS: 'analytics', STAFF: 'staff', NEWSLETTERS: 'newsletters', AUTOMATIONS: 'automations', STRIPE: 'stripe', INTEGRATIONS: 'integrations', PREVIOUS_PAGE: 'previousPage'}}
 */
export const ADMIN_NAVIGATION_DESTINATIONS = {
    THEME: 'theme',
    ANALYTICS: 'analytics',
    STAFF: 'staff',
    NEWSLETTERS: 'newsletters',
    AUTOMATIONS: 'automations',
    STRIPE: 'stripe',
    INTEGRATIONS: 'integrations',
    // Not a route: asks Admin to go back to the page it was on before the
    // billing screen (the payment page's post-success return flow)
    PREVIOUS_PAGE: 'previousPage',
};

/**
 * Application routes used in tests
 */
export const ROUTES = {
    DASHBOARD: '/',
    PLANS: '/plans',
    CHECKOUT: '/checkout',
    SUPPORT: '/support',
    BACKUPS: '/backups',
    DOMAIN: '/domain',
    CUSTOM_DOMAIN: '/domain/custom',
    SENDING_DOMAIN: '/domain/sending',
    UPDATE_CARD: '/update-card',
    // The payment page's return variant: after a successful payment, Ghost
    // Admin is sent back to the page the user came from instead of the
    // billing overview
    UPDATE_CARD_RETURN: '/update-card/return',
};

/**
 * React Router history-state key marking Checkout as an owner-selected
 * continuation after a lapsed-trial owner chooses a paid plan. The key's value
 * must be `true`; direct Checkout navigation does not carry this state.
 * @type {string}
 */
export const LAPSED_TRIAL_CHECKOUT_STATE_KEY = 'lapsedTrialCheckout';

/**
 * Minimum member count we use to determine pricing tiers
 * Used when calculating suitable prices for products based on current members
 * @type {number}
 */
export const MEMBER_MIN_COUNT = 500;

/**
 * Member count increment steps
 * Used when displaying member limit options in the checkout flow
 * @type {number}
 */
export const MEMBER_COUNT_INCREMENT = 500;

/**
 * Audience size above which customers need a custom plan from the Ghost team.
 * @type {number}
 */
export const SELF_SERVE_AUDIENCE_THRESHOLD = 100000;

/**
 * Default product group
 * The default in Daisy is 2021. The product group can be used to create an A/B pricing test.
 * @type {string}
 */
export const DEFAULT_PRODUCT_GROUP = '2021';

/**
 * Stripe.js loading constants
 * Used for lazy-loading Stripe.js with retry logic (fixes BMA-73V)
 */

/**
 * Maximum number of retry attempts for failed Stripe.js loads
 * @type {number}
 */
export const STRIPE_LOAD_RETRY_LIMIT = 3;

/**
 * Maximum retry delay in milliseconds for Stripe.js loading
 * @type {number}
 */
export const STRIPE_LOAD_MAX_DELAY_MS = 4000;

/**
 * Stripe.js retry delay values in milliseconds
 * Each retry attempt uses the corresponding delay: 1st retry = 1s, 2nd = 2s, 3rd = 4s
 * @type {number[]}
 */
export const STRIPE_LOAD_RETRY_DELAYS_MS = [1000, 2000, STRIPE_LOAD_MAX_DELAY_MS];

/**
 * Ad blocker detection threshold in milliseconds
 * Ad blockers typically fail instantly (< 50ms)
 * @type {number}
 */
export const STRIPE_AD_BLOCKER_LOAD_TIME_THRESHOLD_MS = 50;

/**
 * Delay before showing ad blocker error in milliseconds
 * Provides better UX by showing loading state briefly before error
 * @type {number}
 */
export const STRIPE_AD_BLOCKER_RETRY_DELAY_MS = 500;

/**
 * Entri service constants
 * Used for postMessage communication with Entri domain purchasing service
 */

/**
 * Expected origin for Entri postMessage events
 * @type {string}
 */
export const ENTRI_MESSAGE_ORIGINS = ['https://app.goentri.com', 'https://domains.entri.com'];

/**
 * Prefix for Entri postMessage data
 * All Entri messages start with this prefix followed by JSON
 * @type {string}
 */
export const ENTRI_MESSAGE_PREFIX = 'ENTRI_MESSAGE:';

/**
 * Whether we enable Entri's free domain feature
 * @type {boolean}
 */
export const ENTRI_FREE_DOMAINS_ENABLED = true;

/**
 * Checkout flow timing constants
 */

/**
 * Delay in milliseconds for Stripe and Daisy to process webhooks
 * @type {number}
 */
export const WEBHOOK_PROCESSING_DELAY_MS = 7000;

/**
 * Delay in milliseconds to ensure auto price removal is processed in Stripe/Daisy
 * @type {number}
 */
export const AUTO_PRICE_REMOVAL_DELAY_MS = 2000;

/**
 * Delay in milliseconds for Ghost to restart after subscription changes
 * Average boot time is around 3.5 seconds, using 7 seconds for safety
 * @type {number}
 */
export const GHOST_RESTART_DELAY_MS = 7000;

/**
 * Stripe.js error messages
 * Used across components for consistent error handling
 */

/**
 * Error message shown when Stripe.js is blocked by an ad blocker
 * @type {string}
 */
export const STRIPE_AD_BLOCKER_ERROR =
    'Stripe payment processing is blocked by your ad blocker. Disable it for this site and try again.';

/**
 * Error message shown when Stripe.js fails to load due to network issues
 * @type {string}
 */
export const STRIPE_CONNECTION_ERROR =
    'Failed to load Stripe payment processor. Check your internet connection and try again.';

/**
 * Split Test (A/B Testing) constants
 * Used for Netlify's split testing feature via cookies
 */

/**
 * Netlify split test cookie name
 * @type {string}
 */
export const SPLIT_TEST_COOKIE_NAME = 'nf_ab';

/**
 * Regex pattern to extract split test cookie value
 * Matches the cookie name followed by its value (up to the next semicolon)
 * Built dynamically from SPLIT_TEST_COOKIE_NAME to prevent drift
 * Cookie name is escaped to handle any special regex characters safely
 * Uses word boundary (\b) to ensure exact cookie name match, not partial matches
 * @type {RegExp}
 */
export const SPLIT_TEST_COOKIE_PATTERN = new RegExp(
    `(?:^|;\\s*)${escapeRegex(SPLIT_TEST_COOKIE_NAME)}=([^;]+)`,
);

/**
 * Regex pattern to identify Netlify's automatic A/B test assignments
 * Matches numeric values between 0 and 1 (inclusive) with optional decimal places
 * Examples: "0", "0.5", "0.123", "1", "1.0", "1.000"
 * @type {RegExp}
 */
export const SPLIT_TEST_NETLIFY_PATTERN = /^(?:0(?:\.\d+)?|1(?:\.0+)?)$/;

/**
 * Split test cookie expiry duration in months
 * Matches Netlify's default split test cookie lifetime (1 year = 12 months)
 * @type {number}
 */
export const SPLIT_TEST_COOKIE_EXPIRY_MONTHS = 12;

/**
 * Standalone mode testing constants
 * Used when running the app outside of iframe (port 8889) for development/e2e testing
 */

/**
 * Default test email used in standalone mode
 * Used as fallback when no email is provided via parameter, query param, or env var
 * @type {string}
 */
export const STANDALONE_DEFAULT_TEST_EMAIL = 'admin@ghost.org';

/**
 * Stripe test card numbers shared by the unit and e2e suites.
 * - NORMAL never requires authentication (the default card to use).
 * - SCA always requires a 3DS/SCA confirmation.
 * - MASTERCARD is a distinct card for card-swap assertions, where the changed
 *   last4 must be observably different from the seeded NORMAL card.
 * @type {{NORMAL: string, SCA: string, MASTERCARD: string}}
 */
export const STRIPE_TEST_CARDS = {
    NORMAL: '4242 4242 4242 4242',
    SCA: '4000 0027 6000 3184',
    MASTERCARD: '5555 5555 5555 4444',
};

// ============ SSL Status Types ============
/**
 * @typedef {'ACTIVE' | 'CUSTOM' | 'PROXY' | 'VERIFIED' | 'FAILING' | 'FAILED' | 'INVALID' | 'DEFUNCT' | 'PENDING' | 'NONE' | 'CUSTOMNEXT' | 'INITIALIZED'} SSLState
 *
 * @typedef {'ACTIVE' | 'CUSTOM' | 'PROXY' | 'FAILING' | 'FAILED' | 'INVALID' | 'DEFUNCT' | 'PENDING' | 'NONE' | 'CUSTOMNEXT' | null} CustomDomainSSLState
 * @typedef {'FAILED' | 'INVALID' | 'DEFUNCT'} CustomDomainFailedSSLState
 * @typedef {'ACTIVE' | 'PROXY' | 'CUSTOM' | 'FAILING'} CustomDomainSuccessSSLState
 * @typedef {'PENDING' | 'NONE' | 'CUSTOMNEXT'} CustomDomainPendingSSLState
 * @typedef {CustomDomainFailedSSLState | CustomDomainSuccessSSLState} CustomDomainFinalSSLState
 *
 * @typedef {'INITIALIZED' | 'PENDING' | 'NONE' | 'VERIFIED' | 'INVALID' | 'DEFUNCT' | 'CUSTOM' | null} SendingDomainState
 * @typedef {'INVALID' | 'DEFUNCT'} SendingDomainFailedState
 * @typedef {'VERIFIED'} SendingDomainSuccessState
 * @typedef {'PENDING' | 'NONE' | 'INITIALIZED'} SendingDomainPendingState
 * @typedef {'NONE' | 'DEFUNCT'} SendingDomainInactiveState
 * @typedef {SendingDomainFailedState | SendingDomainSuccessState} SendingDomainFinalState
 */

/**
 * All valid SSL states across the application
 * Used as the source of truth for SSL status values
 * @type {Object.<string, SSLState>}
 */
export const VALID_SSL_STATES = {
    ACTIVE: 'ACTIVE',
    CUSTOM: 'CUSTOM',
    PROXY: 'PROXY',
    VERIFIED: 'VERIFIED',
    FAILING: 'FAILING',
    FAILED: 'FAILED',
    INVALID: 'INVALID',
    DEFUNCT: 'DEFUNCT',
    PENDING: 'PENDING',
    NONE: 'NONE',
    CUSTOMNEXT: 'CUSTOMNEXT',
    INITIALIZED: 'INITIALIZED',
};

/**
 * SSL states applicable to custom domains
 * Subset of VALID_SSL_STATES used for custom domain configuration
 * @type {Object.<string, CustomDomainSSLState>}
 */
export const CUSTOM_DOMAIN_SSL_STATES = {
    ACTIVE: VALID_SSL_STATES.ACTIVE,
    CUSTOM: VALID_SSL_STATES.CUSTOM,
    PROXY: VALID_SSL_STATES.PROXY,
    FAILING: VALID_SSL_STATES.FAILING,
    FAILED: VALID_SSL_STATES.FAILED,
    INVALID: VALID_SSL_STATES.INVALID,
    DEFUNCT: VALID_SSL_STATES.DEFUNCT,
    PENDING: VALID_SSL_STATES.PENDING,
    NONE: VALID_SSL_STATES.NONE,
    CUSTOMNEXT: VALID_SSL_STATES.CUSTOMNEXT,
};

/**
 * Custom domain SSL statuses indicating certificate provisioning is in progress
 * Used to show loading/pending UI states during domain setup
 * @type {CustomDomainPendingSSLState[]}
 */
export const CUSTOM_DOMAIN_SSL_PENDING_STATUSES = [
    CUSTOM_DOMAIN_SSL_STATES.PENDING,
    CUSTOM_DOMAIN_SSL_STATES.NONE,
    CUSTOM_DOMAIN_SSL_STATES.CUSTOMNEXT,
];

/**
 * Custom domain SSL statuses indicating certificate is active and working
 * Used to show success states in the domain configuration UI
 * @type {CustomDomainSuccessSSLState[]}
 */
export const CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES = [
    CUSTOM_DOMAIN_SSL_STATES.ACTIVE,
    CUSTOM_DOMAIN_SSL_STATES.CUSTOM,
    CUSTOM_DOMAIN_SSL_STATES.PROXY,
    CUSTOM_DOMAIN_SSL_STATES.FAILING,
];

/**
 * Custom domain SSL statuses indicating certificate provisioning failed
 * Used to show error states and prompt user action
 * @type {CustomDomainFailedSSLState[]}
 */
export const CUSTOM_DOMAIN_SSL_FAILED_STATUSES = [
    CUSTOM_DOMAIN_SSL_STATES.FAILED,
    CUSTOM_DOMAIN_SSL_STATES.INVALID,
    CUSTOM_DOMAIN_SSL_STATES.DEFUNCT,
];

/**
 * Custom domain SSL statuses that represent terminal states (success or failure)
 * Used to stop polling and determine final UI state
 * @type {CustomDomainFinalSSLState[]}
 */
export const CUSTOM_DOMAIN_FINAL_STATUSES = [
    ...CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES,
    ...CUSTOM_DOMAIN_SSL_FAILED_STATUSES,
];

/**
 * Sending domain statuses applicable to sending domains (email)
 * Subset of VALID_SSL_STATES used for sending domain configuration
 * @type {Object.<string, SendingDomainState>}
 */
export const SENDING_DOMAIN_STATES = {
    INITIALIZED: VALID_SSL_STATES.INITIALIZED,
    PENDING: VALID_SSL_STATES.PENDING,
    NONE: VALID_SSL_STATES.NONE,
    VERIFIED: VALID_SSL_STATES.VERIFIED,
    INVALID: VALID_SSL_STATES.INVALID,
    DEFUNCT: VALID_SSL_STATES.DEFUNCT,
};

/**
 * Sending domain statuses indicating DNS verification is in progress
 * Used to show loading/pending UI states during domain setup
 * @type {SendingDomainPendingState[]}
 */
export const SENDING_DOMAIN_PENDING_STATUSES = [
    SENDING_DOMAIN_STATES.INITIALIZED,
    SENDING_DOMAIN_STATES.PENDING,
    SENDING_DOMAIN_STATES.NONE,
];

/**
 * Sending domain statuses indicating domain is verified and active
 * Used to show success states in the sending domain configuration UI
 * @type {SendingDomainSuccessState[]}
 */
export const SENDING_DOMAIN_ACTIVE_STATUSES = [SENDING_DOMAIN_STATES.VERIFIED];

/**
 * Sending domain statuses indicating DNS verification failed
 * Used to show error states and prompt user action
 * @type {SendingDomainFailedState[]}
 */
export const SENDING_DOMAIN_FAILED_STATUSES = [
    SENDING_DOMAIN_STATES.INVALID,
    SENDING_DOMAIN_STATES.DEFUNCT,
];

/**
 * Sending domain statuses that represent terminal states (success or failure)
 * Used to stop polling and determine final UI state
 * @type {SendingDomainFinalState[]}
 */
export const SENDING_DOMAIN_FINAL_STATUSES = [
    ...SENDING_DOMAIN_ACTIVE_STATUSES,
    ...SENDING_DOMAIN_FAILED_STATUSES,
];

/**
 * Sending domain statuses where DNS records should not be prefetched
 * These represent either no domain configured or a removed domain
 * @type {SendingDomainInactiveState[]}
 */
export const SENDING_DOMAIN_INACTIVE_STATUSES = [
    SENDING_DOMAIN_STATES.NONE,
    SENDING_DOMAIN_STATES.DEFUNCT,
];

/**
 * Number of weeks to show the domain warming note after sending domain setup
 * After this period, the note will no longer be displayed
 * @type {number}
 */
export const DOMAIN_WARMING_VISIBILITY_WEEKS = 7;

/**
 * Number of weeks to base the domain warming percentage on
 * This number reflects 100%.
 * @type {number}
 */
export const DOMAIN_WARMING_TOTAL_WEEKS = 6;

export const AUTO_BILLING_ADDON_NAME = 'Autobilling - price updates as your audience grows';
